import { Card, CardHeader, Divider, makeStyles } from "@material-ui/core";
import classnames from "classnames";
import { useCheckMinimumRequiredProps, useDataProvider } from "ra-core";
import { getRecord } from "ra-core/esm/controller/details/useCreateController";
import { TitleForRecord } from "ra-ui-materialui";
import React, { cloneElement, isValidElement, useCallback } from "react";
import { useNotify } from "react-admin";
import {
  DefaultValues,
  FieldValues,
  FormProvider,
  SubmitHandler,
  UnpackNestedValue,
  useForm,
  ValidationMode,
} from "react-hook-form";
import { useLocation } from "react-router";

const useStyles = makeStyles(
  {
    root: {
      width: "100%",
    },
    main: {
      display: "flex",
    },
    noActions: {
      marginTop: "1em",
    },
    card: {
      flex: "1 1 auto",
    },
  },
  { name: "RaCreate" }
);

interface Props<TRecord> {
  title?:
    | string
    | React.ReactElement<any, string | React.JSXElementConstructor<any>>;
  popup?: boolean;
  actions:
    | false
    | React.ReactElement<any, string | React.JSXElementConstructor<any>>;
  resource?: string;
  basePath?: string;
  children?: React.ReactNode;
  className?: string;
  onSuccess?: (args: any) => void;
  record?: DefaultValues<TRecord>;
  margin?: "none" | "dense" | "normal";
  variant?: "standard" | "outlined" | "filled";
  formValidationMode?: keyof ValidationMode;
  createMethodOverride?: (data: any) => Promise<any>;
}

export const HookFormCreateView = <TRecord extends FieldValues>(
  props: Props<TRecord>
) => {
  useCheckMinimumRequiredProps("HookFormCreateView", ["resource"], props);
  const classes = useStyles(props);
  const location = useLocation();
  const {
    resource,
    children,
    actions,
    className,
    basePath,
    title = "",
    popup,
    onSuccess,
    record = getRecord(location, {}),
    formValidationMode = "onBlur",
    createMethodOverride,
    ...rest
  } = props;

  const dataProvider = useDataProvider();
  const formContext = useForm<TRecord>({
    defaultValues: record,
    mode: formValidationMode,
  });

  const notify = useNotify();
  const { handleSubmit: hookFormHandleSubmit } = formContext;

  const createRecord = useCallback(
    (data: UnpackNestedValue<TRecord>) => {
      const createPromise = createMethodOverride
        ? createMethodOverride(data)
        : dataProvider.create(resource!, { data });
      return createPromise.then((result) => {
        if (onSuccess) onSuccess(result);
      });
    },
    [dataProvider, resource, onSuccess, createMethodOverride]
  );

  const handleSubmitInternal: SubmitHandler<TRecord> = (data) => {
    return Promise.resolve(createRecord(data)).catch((e: Error) => {
      notify(e.message ?? "An unexpected error has occurred", "error");
    });
  };

  if (!children) {
    return null;
  }

  return (
    <div className={classnames("create-page", classes.root, className)}>
      {!popup ? <TitleForRecord title={title} record={record} /> : null}
      {actions &&
        cloneElement(actions, {
          basePath,
          //  Ensure we don't override any user provided props
          ...actions.props,
        })}
      <div
        className={classnames(classes.main, {
          [classes.noActions]: !actions || popup,
        })}>
        <Card className={classes.card}>
          {popup ? (
            <>
              <CardHeader title={title}></CardHeader>
              <Divider />
            </>
          ) : null}
          <FormProvider {...formContext}>
            <form
              className={className}
              onSubmit={hookFormHandleSubmit(handleSubmitInternal, (err) =>
                console.error(err)
              )}>
              {React.Children.map(children, (child) => {
                if (isValidElement(child)) {
                  return cloneElement(child, { ...sanitizeRestProps(rest) });
                }
              })}
            </form>
          </FormProvider>
        </Card>
      </div>
    </div>
  );
};

const sanitizeRestProps = ({
  basePath = null,
  defaultTitle = null,
  hasCreate = null,
  hasEdit = null,
  hasList = null,
  hasShow = null,
  history = null,
  loaded = null,
  loading = null,
  location = null,
  match = null,
  onFailure = null,
  onFailureRef = null,
  onSuccess = null,
  onSuccessRef = null,
  options = null,
  permissions = null,
  save = null,
  saving = null,
  setOnFailure = null,
  setOnSuccess = null,
  setTransform = null,
  transform = null,
  transformRef = null,
  ...rest
}: any) => rest;
